/* eslint-disable max-len */
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import Button from '@mui/material/Button';
// import Grid from '@mui/material/Grid';
import styled from 'styled-components';
import IconButton from '@digital-hig/mui/dist/IconButton';
import { unwrapResult } from '@reduxjs/toolkit';
import { Link } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import Icon from '@mui/material/SvgIcon';
import Bookmark from '@digital-hig/icon/lib/esm/icons/bookmark';
import BookmarkFilled from '@digital-hig/icon/lib/esm/icons/bookmark-filled';
import Add from '@digital-hig/icon/lib/esm/icons/add';
import ListModal from './listModal';

import {
  addListItem,
  deleteListItem, getLearningRecords,
  getLists,
  getOA2Token, getUserId,
} from '../store/userSlice';
import { sendMessage } from '../store/messagingSlice';
// import { LoginClickHandler } from '../common/login';
import {
  ENVIRONMENT,
  LIST_ID_BOOKMARKS,
  LIST_TYPE_COURSE,
  LIST_TYPE_MODULE,
  LIST_TYPE_COLLECTION,
  VERSION,
  CLIENT_SOURCE_LRNO,
} from '../common/constants';
import EnvContext from '../common/envContext';
import { generateLRSID, serializeElement } from '../common/utilities';
import {
  generateCourseDataAnalyticsString, triggerManualLinkClick, triggerLearningPlanAddToList, triggerLearningPlanBookmark,
} from '../common/analyticsApi';

const ListCTA = ({
  data, isCourse, hasLabels, isCollection, analyticsArea,
}) => {
  const [isBookmarked, setisBookmarked] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const appVersion = envContext[VERSION];
  const uid = useSelector(getUserId);
  const oa2Token = useSelector(getOA2Token);
  const dispatch = useDispatch();
  const lists = useSelector(getLists);
  const { id } = data;
  const learningRecords = useSelector(getLearningRecords);
  // eslint-disable-next-line no-nested-ternary
  const listDataType = isCourse ? LIST_TYPE_COURSE : (isCollection ? LIST_TYPE_COLLECTION : LIST_TYPE_MODULE);

  const triggerClickTracking = ({ action }) => {
    if (analyticsArea === 'learningplan') {
      if (action === 'add to list') {
        triggerLearningPlanAddToList({
          data, learningRecords, env,
        });
      } else if (action === 'add to bookmarks' || action === 'remove from bookmarks') {
        triggerLearningPlanBookmark({
          data, learningRecords, env, isBookmarked: action === 'remove from bookmarks',
        });
      }
    } else {
      triggerManualLinkClick({
        linkTitle: `${analyticsArea}:${action}:${generateCourseDataAnalyticsString({
          data, env, learningRecords,
        })}`,
      });
    }
  };

  // Every time lists is updated check for bookmarks
  useEffect(() => {
    const bookmarks = lists?.find((list) => list.listId === LIST_ID_BOOKMARKS);
    if (bookmarks?.listItems?.length) {
      const matchingItem = bookmarks.listItems.find((item) => item?.resourceId?.endsWith(id));
      if (matchingItem && !isBookmarked) {
        setisBookmarked(true);
      } else if (!matchingItem && isBookmarked) {
        setisBookmarked(false);
      }
    } else if (isBookmarked) {
      setisBookmarked(false);
    }
  }, [lists]);

  const handleBookmark = () => {
    setBtnDisabled(true);
    triggerClickTracking({ action: isBookmarked ? 'remove from bookmarks' : 'add to bookmarks' });
    if (uid) {
      const bookmarks = lists?.find((list) => list.listId === LIST_ID_BOOKMARKS);
      if (isBookmarked) { // Remove from bookmarks
        dispatch(deleteListItem({
          env, listObject: bookmarks, listItemId: generateLRSID({ env, id }), oa2Token, type: listDataType, appVersion,
        }))
          .then(unwrapResult)
          .then(() => dispatch(sendMessage({
            type: 'success',
            children: serializeElement(
              <>
                {'Your bookmarks have been updated. You can access your bookmarks via '}
                <Link to="/dashboard#myBookmarks">My dashboard.</Link>
              </>,
            ),
          })),
          () => dispatch(sendMessage({
            type: 'error',
            children: serializeElement(
              <>
                Your bookmarks have not been updated.  Please try again.
              </>,
            ),
          })))
          .then(() => setBtnDisabled(false));
      } else { // Add to existing bookmarks list
        dispatch(addListItem({
          env, listObject: bookmarks || { listId: LIST_ID_BOOKMARKS, listItems: [], targetChannels: CLIENT_SOURCE_LRNO }, listItemId: generateLRSID({ env, id }), oa2Token, type: listDataType, appVersion,
        }))
          .then(unwrapResult)
          .then(() => dispatch(sendMessage({
            type: 'success',
            children: serializeElement(
              <>
                {'Your bookmarks have been updated. You can access your bookmarks via '}
                <Link to="/dashboard#myBookmarks">My dashboard.</Link>
              </>,
            ),
          })),
          () => dispatch(sendMessage({
            type: 'error',
            children: serializeElement(
              <>
                Your bookmarks have not been updated.  Please try again.
              </>,
            ),
          })))
          .then(() => setBtnDisabled(false));
      }
    } else {
      dispatch(sendMessage({
        type: 'warning',
        children: serializeElement(
          <>
            You must be signed in for this action.
            <br />
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" data-functions={{ onClick: 'loginClickHandler' }}>Sign in</a>
          </>,
        ),
      }));
      setBtnDisabled(false);
    }
  };

  const handleCreateListAction = () => {
    triggerClickTracking({ action: 'add to list' });
    if (uid) {
      setShowListModal(true);
    } else {
      dispatch(sendMessage({
        type: 'warning',
        children: serializeElement(
          <>
            You must be signed in for this action.
            <br />
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" data-functions={{ onClick: 'loginClickHandler' }}>Sign in</a>
          </>,
        ),
      }));
    }
  };

  return (
    <RootElement className="listCTA">
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      {hasLabels ? (
        <Button
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleCreateListAction();
          }}
          startIcon={<Icon id="dhig--icons--utility--add" fontSize="medium"><Add /></Icon>}
        >
          {hasLabels && 'Add to list'}
        </Button>
      )
        : (
          <IconButton
            size="medium"
            variant="text"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleCreateListAction();
            }}
          >
            <Icon id="dhig--icons--utility--add" fontSize="medium"><Add /></Icon>
          </IconButton>
        ) }

      {hasLabels ? (
        <Button
          variant="text"
          disabled={btnDisabled}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleBookmark();
          }}
          // eslint-disable-next-line max-len
          startIcon={!isBookmarked ? <Icon fontSize="medium" id="dhig--icons--actions--bookmark"><Bookmark /></Icon> : <Icon id="dhig--icons--actions--bookmark-filled" fontSize="medium"><BookmarkFilled /></Icon>}
        >
          {hasLabels && 'Bookmark'}
        </Button>
      )
        : (
          <IconButton
            size="medium"
            disabled={btnDisabled}
            variant="text"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleBookmark();
            }}
          >
            {!isBookmarked ? <Icon id="dhig--icons--actions--bookmark" fontSize="medium"><Bookmark /></Icon> : <Icon id="dhig--icons--actions--bookmark-filled" fontSize="medium"><BookmarkFilled /></Icon>}
          </IconButton>
        ) }

      {showListModal ? (<ListModal displayModal={setShowListModal} data={{ ...data, objectType: listDataType }} analyticsArea={analyticsArea} />) : null}
    </RootElement>
  );
};

const RootElement = styled.div`
  button, button:hover, button:focus, button:active, button:active:focus {
    padding: 9px !important;
    margin-left: 7px !important;
    min-width: 30px !important;
  }
`;

ListCTA.propTypes = {
  data: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  isCourse: PropTypes.bool,
  hasLabels: PropTypes.bool,
  isCollection: PropTypes.bool,
  analyticsArea: PropTypes.string,
};

ListCTA.defaultProps = {
  isCourse: true,
  hasLabels: undefined,
  isCollection: false,
  analyticsArea: 'undefined',
};
export default ListCTA;
